import React, { useState, useEffect } from 'react';
import { customBaseUrl, httpGet } from '../../store/http';
import { NotificationManager } from 'react-notifications';
import {
  Input,
  Button,
  Form,
  Row,
  Col,
  Card,
  Typography,
  Space,
  Select,
  Tag,
  Empty,
  Divider
} from 'antd';
import { DataGrid } from '@mui/x-data-grid';
import { Box } from '@mui/material';
import AdminLayout from "../../components/Layout";
import LoanDashboardLayout from '../../components/Layout/LoanDashboardLayout';
import { showLoader, hideLoader } from '../../utils/loader';
import { useNavigate, useLocation } from 'react-router-dom';
import slugs from '../../resources/slugs';
import { useMatStyles, CardWrapper } from './style';
import { SearchOutlined, FilterOutlined } from '@ant-design/icons';

const { Title, Text } = Typography;
const { Option } = Select;

const AllLoansPage = ({ from }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const classes = useMatStyles();
  const { initialStatus } = location.state || {};

  const [loans, setLoans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [pagination, setPagination] = useState({ page: 0, limit: 10 });
  const [totalRows, setTotalRows] = useState(0);
  const [filters, setFilters] = useState({
    loanId: '',
    accountNumber: '',
    email: '',
    status: initialStatus || '',
  });

  const fetchLoans = async (currentFilters, currentPage, currentPageSize) => {
    // Determine if we have any search/filter criteria
    const hasCriteria = Object.values(currentFilters).some(val => val !== '');

    if (!hasCriteria) {
      setLoans([]);
      setTotalRows(0);
      setHasSearched(false);
      return;
    }

    setLoading(true);
    setHasSearched(true);
    showLoader();
    try {
      const token = from === 'admin' ? localStorage.getItem('token') : localStorage.getItem('loanAdminToken');
      if (!token) {
        window.location.href = '/loan-admin-login';
        return;
      }

      const queryParams = new URLSearchParams({
        page: currentPage + 1,
        limit: currentPageSize,
      });

      // Use specific endpoint for status filtering
      let endpoint = `${customBaseUrl.loanUrl}/api/v1/admin/all-loans`;

      // If ONLY status is selected, use the status-specific endpoint
      const otherFilters = { ...currentFilters };
      delete otherFilters.status;
      const hasOtherFilters = Object.values(otherFilters).some(val => val !== '');

      if (currentFilters.status && !hasOtherFilters) {
        endpoint = `${customBaseUrl.loanUrl}/api/v1/admin/loans-by-status`;
        queryParams.append('status', currentFilters.status);
      } else {
        // Search mode - combine all available filters
        if (currentFilters.loanId) queryParams.append('loanId', currentFilters.loanId);
        if (currentFilters.email) queryParams.append('email', currentFilters.email);
        if (currentFilters.accountNumber) queryParams.append('accountNumber', currentFilters.accountNumber);
        if (currentFilters.status) queryParams.append('status', currentFilters.status);
      }

      const res = await httpGet(
        `${endpoint}?${queryParams.toString()}`,
        null, false, false, token
      );

      if (res.status && res.data) {
        setLoans(res.data || []);
        setTotalRows(res.pagination?.total || res.data.length || 0);
      } else {
        setLoans([]);
        setTotalRows(0);
        if (res.message && res.message !== "Internal Server Error") {
          NotificationManager.info(res.message);
        }
      }
    } catch (error) {
      console.error('Error fetching loans:', error);
      NotificationManager.error('Error fetching loans');
    } finally {
      setLoading(false);
      hideLoader();
    }
  };

  useEffect(() => {
    if (hasSearched || filters.status) {
      fetchLoans(filters, pagination.page, pagination.limit);
    }
  }, [pagination.page, pagination.limit]);

  // Handle initial status if passed from dashboard
  useEffect(() => {
    if (initialStatus) {
      handleSearch();
    }
  }, [initialStatus]);

  const handleFilterChange = (name, value) => {
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const handleSearch = () => {
    setPagination(prev => ({ ...prev, page: 0 }));
    fetchLoans(filters, 0, pagination.limit);
  };

  const handleClear = () => {
    setFilters({
      loanId: '',
      accountNumber: '',
      email: '',
      status: '',
    });
    setLoans([]);
    setTotalRows(0);
    setHasSearched(false);
  };

  const columns = [
    {
      field: 'loan_id',
      headerName: 'Loan ID',
      width: 150,
      renderCell: (params) => <Text strong style={{ color: '#FF4B01' }}>{params.value || params.row.id}</Text>
    },
    {
      field: 'customer_name',
      headerName: 'Customer Name',
      width: 200,
      renderCell: (params) => <Text>{`${params.row.first_name || ''} ${params.row.last_name || ''}`}</Text>
    },
    {
      field: 'phone',
      headerName: 'Phone Number',
      width: 150,
      renderCell: (params) => <Text>{params.row.phone || 'N/A'}</Text>
    },
    { field: 'type', headerName: 'Type', width: 120 },
    {
      field: 'amount',
      headerName: 'Amount',
      width: 130,
      renderCell: (params) => <Text>₦{Number(params.value).toLocaleString()}</Text>
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 180,
      renderCell: (params) => {
        const statusColors = {
          'Approved': 'green',
          'Disbursed': 'blue',
          'Completed': 'cyan',
          'Pending': 'orange',
          'Rejected': 'red',
          'Refunded': 'purple',
          'Offer Letter Signed': 'geekblue',
          'Offer Letter Sent': 'gold',
          'Pending Loan Officer Approval': 'magenta',
          'Pending Finance Approver Approval': 'volcano',
        };
        return (
          <Tag color={statusColors[params.value] || 'default'} style={{ borderRadius: 6 }}>
            {params.value}
          </Tag>
        );
      }
    },
    {
      field: 'created_at',
      headerName: 'Date Created',
      width: 180,
      valueFormatter: ({ value }) => value ? new Date(value).toLocaleDateString() : '-',
    },
    {
      field: 'action',
      headerName: 'Action',
      width: 120,
      sortable: false,
      renderCell: (params) => (
        <Button
          type="primary"
          size="small"
          style={{ borderRadius: 6, background: '#1a1a1a', border: 'none' }}
          onClick={() => navigate(slugs.loan_view.replace(':id', params.row.loan_id || params.row.id), { state: { from } })}
        >
          View
        </Button>
      ),
    },
  ];

  const Layout = from === "admin" ? AdminLayout : LoanDashboardLayout;

  return (
    <Layout>
      <div style={{ padding: '0 32px 32px 32px' }}>
        <div style={{ marginBottom: 32 }}>
          <Title level={2} style={{ margin: 0, fontWeight: 800 }}>Search Loan Requests</Title>
          <Text type="secondary">Enter search criteria to find specific loan applications</Text>
        </div>

        <CardWrapper>
          <Card style={{ marginBottom: 24, borderRadius: 16 }}>
            <Form layout="vertical">
              <Row gutter={[24, 16]}>
                <Col xs={24} md={6}>
                  <Form.Item label={<Text strong style={{ fontSize: 12 }}>LOAN ID</Text>}>
                    <Input
                      placeholder="e.g. LN-123"
                      value={filters.loanId}
                      onChange={(e) => handleFilterChange('loanId', e.target.value)}
                      prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} md={6}>
                  <Form.Item label={<Text strong style={{ fontSize: 12 }}>EMAIL ADDRESS</Text>}>
                    <Input
                      placeholder="user@example.com"
                      value={filters.email}
                      onChange={(e) => handleFilterChange('email', e.target.value)}
                      prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} md={6}>
                  <Form.Item label={<Text strong style={{ fontSize: 12 }}>ACCOUNT NUMBER</Text>}>
                    <Input
                      placeholder="10-digit number"
                      value={filters.accountNumber}
                      onChange={(e) => handleFilterChange('accountNumber', e.target.value)}
                      maxLength={10}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} md={6}>
                  <Form.Item label={<Text strong style={{ fontSize: 12 }}>STATUS</Text>}>
                    <Select
                      placeholder="Filter by status"
                      value={filters.status}
                      onChange={(val) => handleFilterChange('status', val)}
                      allowClear
                      style={{ width: '100%' }}
                    >
                      <Option value="Pending">Pending</Option>
                      <Option value="Approved">Approved</Option>
                      <Option value="Rejected">Rejected</Option>
                      <Option value="Disbursed">Disbursed</Option>
                      <Option value="Completed">Completed</Option>
                      <Option value="Refunded">Refunded</Option>
                      <Option value="Offer Letter Sent">Offer Letter Sent</Option>
                      <Option value="Offer Letter Signed">Offer Letter Signed</Option>
                      <Option value="Pending Loan Officer Approval">Pending Loan Officer Approval</Option>
                      <Option value="Pending Finance Approver Approval">Pending Finance Approver Approval</Option>
                    </Select>
                  </Form.Item>
                </Col>
              </Row>
              <Divider style={{ margin: '12px 0 24px 0' }} />
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
                <Button onClick={handleClear} style={{ borderRadius: 10, height: 40, padding: '0 24px' }}>Clear All</Button>
                <Button
                  type="primary"
                  icon={<SearchOutlined />}
                  onClick={handleSearch}
                  style={{ borderRadius: 10, height: 40, padding: '0 24px', background: '#FF4B01', border: 'none' }}
                >
                  Search Records
                </Button>
              </div>
            </Form>
          </Card>
        </CardWrapper>

        {hasSearched ? (
          <Box sx={{
            height: 600,
            width: '100%',
            '& .MuiDataGrid-root': {
              border: 'none',
              background: '#fff',
              borderRadius: '16px',
              boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
              '& .MuiDataGrid-cell:focus': { outline: 'none' }
            }
          }}>
            <DataGrid
              getRowId={(row) => row.loan_id || row.id}
              rows={loans}
              columns={columns}
              loading={loading}
              pagination
              paginationMode="server"
              rowCount={totalRows}
              pageSize={pagination.limit}
              page={pagination.page}
              onPageChange={(newPage) => setPagination(prev => ({ ...prev, page: newPage }))}
              onPageSizeChange={(newPageSize) => setPagination({ page: 0, limit: newPageSize })}
              rowsPerPageOptions={[10, 20, 50]}
              disableSelectionOnClick
            />
          </Box>
        ) : (
          <div style={{ padding: '80px 0', textAlign: 'center', background: '#fff', borderRadius: 16, border: '1px dashed #e0e0e0' }}>
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={
                <Space direction="vertical">
                  <Text strong style={{ fontSize: 18, color: '#1a1a1a' }}>Ready to Search</Text>
                  <Text type="secondary">Enter search criteria above to retrieve loan records.</Text>
                </Space>
              }
            >
              <Button type="primary" ghost icon={<FilterOutlined />} onClick={() => { handleFilterChange('status', 'Pending'); handleSearch(); }}>
                Show All Pending Loans
              </Button>
            </Empty>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default AllLoansPage;
